

import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Card } from "@/components/Card/Card";
import { Spacing } from "@/components/Spacing/Spacing";
import { motion, useIsPresent, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Children, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormInput } from "../Inputs/FormInput";
import { IconX } from "@tabler/icons-react";
import { ModalErrorSummary } from "@/components/ModalErrorSummary/ModalErrorSummary";
import { useFocusTrap } from "@/lib/a11y/useFocusTrap";
import { useBackdropPressDismiss } from "@/lib/a11y/backdropPress";

interface PDeleteConfirmationProps {
  title?: string;
  message: string;
  /**
   * UXW12 — optional JSX rendered below `message`. Used to show e.g. a
   * preview of the items being deleted (per-pipe names list on the
   * automation bulk-delete confirmation). Backwards-compatible: existing
   * callers don't pass this and see no change.
   */
  children?: React.ReactNode;
  onCloseAction: (e: React.MouseEvent) => void;
  /** `typed` is what the user entered in the confirmation input (when one is shown). */
  onConfirmAction: (e: React.MouseEvent, typed?: string) => Promise<void>;
  isLoading: boolean;
  primaryButtonText?: string;
  primaryButtonColor?: string;
  primaryButtonTextColor?: string;
  confirmationText?: string;
  confirmationPlaceholder?: string;
  confirmationLabel?: string;
  error?: string;
  /** Raise above a high-z surface (AI studio overlay at z-10000). Default z-2000. */
  elevated?: boolean;
  /**
   * The message (and `children`) can change while the dialog is open — e.g.
   * the remove-member confirmation swaps to the offboarding preview's text
   * once it lands. Wraps both in ONE polite, atomic live region that exists
   * from the first frame, so a screen reader hears the new text. Absent =
   * today's markup.
   */
  liveMessage?: boolean;
  /**
   * Hold the confirm button disabled on top of the usual gates (the text it
   * confirms is still being settled). Absent = today's behaviour.
   */
  confirmDisabled?: boolean;
  /**
   * While `confirmDisabled`, keep the confirm button in the tab order
   * (`aria-disabled`, PrimaryButton's `focusableWhenDisabled`) — for a gate
   * the user has to find out about, e.g. a required tick (PB-CDP-7). Pair it
   * with `confirmDescribedBy`. Absent = today's behaviour.
   */
  focusableWhenDisabled?: boolean;
  /** Id(s) of what explains the confirm button's state (`aria-describedby`). */
  confirmDescribedBy?: string;
  /**
   * No Cancel button — the primary is the only way out (a DONE state that
   * just reports, e.g. the guest-trash restore's result warnings behind a
   * "Close"). Escape still calls `onCloseAction`. Absent = today's two buttons.
   */
  hideCancel?: boolean;
  /** `data-testid` of the message paragraph (W6: `guest-trash-confirm-copy` on the trash sentence). */
  messageTestId?: string;
  /** `data-testid` of the confirm button; default `root-delete-confirm-ok`. */
  confirmTestId?: string;
  /**
   * `data-testid` of the typed-confirmation input. The input also carries
   * `data-confirm-word` = the phrase, so a locale-blind caller (e2e) can read
   * the word it has to type.
   */
  confirmationInputTestId?: string;
}

// ⚠ Never add useSuppressCoach() here: this dialog is rendered INSIDE the
// floating background-jobs panel (JobRow's cancel confirm), which unmounts
// itself while overlayCount > 0 — suppressing from in here would instantly
// unmount this very dialog. It stacks above the floaters by z-index instead.
export const PDeleteConfirmation: React.FC<PDeleteConfirmationProps> = ({
  title = "Delete Confirmation",
  message,
  children,
  onCloseAction,
  onConfirmAction,
  isLoading,
  primaryButtonText,
  confirmationText,
  confirmationPlaceholder,
  confirmationLabel,
  primaryButtonColor,
  primaryButtonTextColor,
  error,
  elevated = false,
  liveMessage = false,
  confirmDisabled = false,
  focusableWhenDisabled = false,
  confirmDescribedBy,
  hideCancel = false,
  messageTestId,
  confirmTestId,
  confirmationInputTestId,
}) => {
  const t = useTranslations("delete-popup");
  const [inputValue, setInputValue] = useState("");
  const reduceMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // False from the moment a host's AnimatePresence starts the exit animation
  // (always true outside one). The marker, the Escape listener and the focus
  // trap end THEN, not ~200 ms later when the animation does: a quick second
  // Escape meant for the host was swallowed by a confirm already on its way
  // out (the marker made the host stand down, focus still sat in here).
  const isPresent = useIsPresent();
  // Focus the confirm input when present; otherwise the trap focuses the
  // first control (close/Cancel) — never pre-focuses the destructive button.
  // cycleTab: WebKit does not stop on buttons, so from a field inside (the
  // track delete's move-target picker) Tab left the confirm for the page
  // behind it — the trap now walks its own order, buttons included.
  useFocusTrap(dialogRef, isPresent, confirmationText ? inputRef : undefined, {
    cycleTab: true,
  });

  // The preview box scrolls once its content outgrows it. The trap's Tab
  // order has no stop for a plain scroll container (and it replaced the
  // browser's own scroller stop), so an overflowing box becomes a labelled,
  // focusable region — arrows / Page keys scroll it. Measured, not assumed:
  // a box that fits (one move-target picker) must not add a dead stop.
  const messageId = useId();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewContentRef = useRef<HTMLDivElement | null>(null);
  const [previewScrolls, setPreviewScrolls] = useState(false);
  useLayoutEffect(() => {
    const box = previewRef.current;
    if (!box) {
      setPreviewScrolls(false);
      return;
    }
    const measure = () => setPreviewScrolls(box.scrollHeight > box.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    // The box itself (viewport-capped height) AND its content (an expandable
    // recipient list grows inside a box that no longer changes size).
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    if (previewContentRef.current) observer.observe(previewContentRef.current);
    return () => observer.disconnect();
  }, [children]);

  useEffect(() => {
    if (!isPresent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Something inside owns this key — an open picker list (the track
      // delete's move-target dropdown), a calendar. Capture on `window` runs
      // BEFORE the child's own handler, so without this carve-out the first
      // Escape aimed at the list closed the whole confirm (same contract as
      // Modal / HubDialogShell / ActivityDialog). Not stopped either: the
      // owner's handler has to receive the key.
      const target = e.target;
      if (target instanceof Element && target.closest('[data-escape-owner="true"]')) return;
      // Focus is not always INSIDE the owner (a press on the list can leave
      // it on <body> while the list is still open) — ask the panel too.
      if (dialogRef.current?.querySelector('[data-escape-owner="true"]')) return;
      // Close only this confirm (it may be nested over another modal).
      e.stopPropagation();
      if (!isLoading) onCloseAction({} as React.MouseEvent);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isLoading, onCloseAction, isPresent]);

  const isConfirmationValid = confirmationText
    ? inputValue.trim() === confirmationText.trim()
    : true;

  // Same gate as Escape and Cancel: while the action is in flight the dialog
  // IS the progress indicator — closing it leaves the caller's request
  // running with nothing on screen saying so. PB-CDP-7 (audit r1, LOW-2):
  // the backdrop now scrolls, so only a press AND a click on its client area
  // close — never one on its scrollbar, never a drag out of the dialog.
  const backdrop = useBackdropPressDismiss(onCloseAction, !isLoading);

  const submitOnEnter = () => {
    if (isConfirmationValid && !isLoading && !confirmDisabled) onConfirmAction({} as React.MouseEvent, inputValue);
  };

  const handleConfirm = async (e: React.MouseEvent) => {
    if (isConfirmationValid && !confirmDisabled) {
      await onConfirmAction(e, inputValue);
    }
  };

  const messageBlock = (
    <>
      <p
        id={messageId}
        className="body-1-regular font-secondary w-full whitespace-normal"
        data-testid={messageTestId}
      >
        {message}
      </p>

      {/* UXW12 — optional preview of items (e.g. pipe names list).
          Height-capped with internal scroll so an expandable preview
          (opt-in recipient list) can never push the popup off-screen.
          While it scrolls it is a keyboard stop (see previewScrolls). */}
      {/* `children` is often an ARRAY of conditionals (`{a ? <X/> : null}{b ? <Y/> : null}`),
          truthy even when every slot is null — then the spacer and the empty region
          rendered for nothing (the trash dialog with no consequence to list, 22.10.).
          Children.toArray drops null/undefined/boolean slots — but KEEPS "" and 0
          (a bare string or number child mounts the region), and a fragment around
          nulls is one rendering element: pass the slots as siblings, guard with
          booleans or `x ? <X/> : null`, never with `count && <X/>`. */}
      {Children.toArray(children).length > 0 && (
        <>
          <Spacing height="h-3" />
          <div
            ref={previewRef}
            className="w-full max-h-[50dvh] overflow-y-auto focus-ring rounded-lg"
            {...(previewScrolls
              ? {
                  tabIndex: 0,
                  role: "region",
                  "aria-labelledby": messageId,
                }
              : {})}
          >
            <div ref={previewContentRef}>{children}</div>
          </div>
        </>
      )}
    </>
  );

  const modalContent = (
    <motion.div
      // Marker for hosts that own a window-level Escape (HubDialogShell,
      // useOverlayEscape): this confirm portals to <body>, and once focus
      // falls to <body> (a picked list option blurs its input) the host could
      // not tell the key is meant for THIS dialog and closed itself — the
      // track manager vanished under its own delete question. Dropped the
      // moment the close starts (isPresent), not after the exit animation.
      data-nested-dialog={isPresent ? "true" : undefined}
      // PB-CDP-7: a dialog taller than the screen (a phone, a long notice
      // and its tick) scrolls the overlay; `my-auto` on the panel centres it
      // while it fits and lets it start at the top once it does not — a
      // centred flex item would overflow upwards, out of reach.
      className={`fixed left-0 top-0 bg-black/50 flex items-center justify-center h-dvh w-screen overflow-y-auto overscroll-contain py-4 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${elevated ? "z-[10060]" : "z-2000"} cursor-default`}
      onMouseDownCapture={backdrop.onMouseDownCapture}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={backdrop.onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="outline-none my-auto"
        initial={reduceMotion ? false : { opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
        transition={
          reduceMotion ? { duration: 0 } : { type: "spring", damping: 20, stiffness: 300 }
        }
      >
        <Card
          padding="p-6"
          width="w-[95vw] sm:max-w-125"
          rounded="rounded-3xl"
          shadow="shadow-xl"
          header={title}
          icon={IconX}
          iconAriaLabel={t("cancel")}
          iconActionOnClick={(e) => {
            if (!isLoading) onCloseAction(e);
          }}
        >
          <div className="flex flex-col items-center w-full">
            <Spacing height={confirmationText ? "h-6" : "h-12"} />
            {liveMessage ? (
              <div
                className="flex flex-col items-center w-full"
                aria-live="polite"
                aria-atomic="true"
                aria-busy={confirmDisabled ? "true" : undefined}
                data-testid="delete-confirm-live"
              >
                {messageBlock}
              </div>
            ) : (
              messageBlock
            )}

            {confirmationText && (
              <>
                <Spacing height="h-6" />
                <div className="w-full">
                  <FormInput
                    labelFontSize="body-2-regular"
                    id="delete-confirmation-input"
                    ref={inputRef}
                    label={
                      confirmationLabel ||
                      `${t("type")} "${confirmationText}" ${t("toConfirm")}`
                    }
                    placeholder={confirmationPlaceholder || confirmationText}
                    value={inputValue}
                    onChangeAction={setInputValue}
                    width="w-full"
                    error={
                      inputValue.length > 0 && !isConfirmationValid
                        ? t("confirmationError")
                        : undefined
                    }
                    onEnterAction={submitOnEnter}
                    data-testid={confirmationInputTestId}
                    inputProps={
                      confirmationInputTestId
                        ? { "data-confirm-word": confirmationText }
                        : undefined
                    }
                  />
                </div>
              </>
            )}

            <Spacing height="h-12" />
            <div className="flex flex-row w-full space-x-3">
              {!hideCancel && (
                <SecondaryButton
                  text={t("cancel")}
                  onClick={(e) => onCloseAction(e)}
                  disabled={isLoading}
                  width="w-1/2"
                />
              )}
              <PrimaryButton
                loading={isLoading}
                text={primaryButtonText ? primaryButtonText : t("delete")}
                onClick={handleConfirm}
                width={hideCancel ? "w-full" : "w-1/2"}
                color={
                  primaryButtonColor
                    ? primaryButtonColor
                    : primaryButtonText
                      ? undefined
                      : "surface-error-dark"
                }
                textColor={
                  primaryButtonTextColor
                    ? primaryButtonTextColor
                    : "font-light-primary"
                }
                disabled={!isConfirmationValid || isLoading || confirmDisabled}
                focusableWhenDisabled={focusableWhenDisabled}
                ariaDescribedBy={confirmDescribedBy}
                testId={confirmTestId ?? "root-delete-confirm-ok"}
              />
            </div>
            <ModalErrorSummary errors={[error]} />
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
